import { useState, useEffect, useCallback } from 'react';
import styles from './WaitlistModal.module.css';

const STORAGE_KEY = 'cmpnd_waitlist_draft';

const COMPANY_SIZES = [
  { value: 'solo', label: 'Solo' },
  { value: '2-10', label: '2–10' },
  { value: '11-50', label: '11–50' },
  { value: '51-200', label: '51–200' },
  { value: '201-1000', label: '201–1,000' },
  { value: '1000+', label: '1,000+' },
];

function loadDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveDraft(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // silently fail
  }
}

function clearDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // silently fail
  }
}

export default function WaitlistModal({ open, onClose }) {
  const [companySize, setCompanySize] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  // Load draft on mount
  useEffect(() => {
    const draft = loadDraft();
    if (draft.companySize) setCompanySize(draft.companySize);
    if (draft.email) setEmail(draft.email);
  }, []);

  // Persist draft on change
  useEffect(() => {
    if (status !== 'success') {
      saveDraft({ companySize, email });
    }
  }, [companySize, email, status]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!companySize || !email) return;

      setStatus('submitting');
      setErrorMsg('');

      try {
        const res = await fetch('/api/waitlist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), companySize }),
        });

        const data = await res.json();

        if (!res.ok) {
          setStatus('error');
          setErrorMsg(data.error || 'Something went wrong.');
          return;
        }

        setStatus('success');
        clearDraft();
      } catch {
        setStatus('error');
        setErrorMsg('Network error. Please try again.');
      }
    },
    [companySize, email],
  );

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.headerLabel}>JOIN_WAITLIST</span>
          <button className={styles.close} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {status === 'success' ? (
          <div className={styles.body}>
            <div className={styles.successBlock}>
              <p className={styles.successTitle}>&gt; REGISTERED</p>
              <p className={styles.successText}>
                You're on the list. We'll be in touch.
              </p>
            </div>
            <button className={styles.submitButton} onClick={onClose}>
              CLOSE
            </button>
          </div>
        ) : (
          <form className={styles.body} onSubmit={handleSubmit}>
            <fieldset className={styles.fieldset}>
              <legend className={styles.legend}>
                How many engineers at your company?
              </legend>
              <div className={styles.options}>
                {COMPANY_SIZES.map((size) => (
                  <label
                    key={size.value}
                    className={`${styles.option} ${companySize === size.value ? styles.optionSelected : ''}`}
                  >
                    <input
                      type="radio"
                      name="companySize"
                      value={size.value}
                      checked={companySize === size.value}
                      onChange={() => setCompanySize(size.value)}
                      className={styles.radio}
                    />
                    <span className={styles.optionLabel}>{size.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="waitlist-email">
                Email
              </label>
              <input
                id="waitlist-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className={styles.input}
                autoComplete="email"
              />
            </div>

            {errorMsg && <p className={styles.error}>{errorMsg}</p>}

            <button
              type="submit"
              className={styles.submitButton}
              disabled={!companySize || !email || status === 'submitting'}
            >
              {status === 'submitting' ? 'SUBMITTING...' : '> SUBMIT'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
